<template>
  <div class="content-delegate">
    <div class="content-delegate__top">
      <div
        @click="isAuthenticated ? toggleIsContentEngaged() : null"
        :class="[
          'content-delegate__tile',
          isAuthenticated && 'content-delegate__tile--allowed',
        ]"
        :style="`background: linear-gradient(${$getGradient()})`"
      >
        <component :is="contentIcon" class="content-delegate__tile--icon" />
      </div>
      <div class="content-delegate__details">
        <p class="content-delegate__details--title">
          {{ contentTitle }}
        </p>
        <p class="lead">
          Content:
          <span class="data">{{ friendlyContentType }}</span>
        </p>
        <p v-if="contentDuration && contentDuration.length" class="lead">
          {{ friendlyContentType }} Duration:
          <span class="data content-delegate__details--duration">{{
            contentDuration
          }}</span>
        </p>
      </div>
      <div class="content-delegate__btn-wrapper">
        <button
          v-if="isAuthenticated"
          class="btn btn--secondary"
          @click="toggleIsContentEngaged"
        >
          {{ buttonText }}
        </button>
        <template v-else>
          <a class="btn btn--secondary" :href="`//${plansLink}`">
            <LockIcon />
            Get access
          </a>
        </template>
      </div>
    </div>
    <div v-if="isContentEngaged" class="content-delegate__media">
      <ActionLoader
        v-show="isContentLoading"
        :loaderClass="'content-delegate__media-loader'"
      />
      <MediaDelegate
        v-bind="{
          parentType: 'learning-path',
          content: { media: [content], ...content },
          contentTitle,
          userCompletionStatus: currentStatus,
          canStartIssue: true,
          hasAccess: isAuthenticated,
        }"
        @toggle-quiz="isContentEngaged = false"
        @is-loading="isContentLoading = !isContentLoading"
      />
    </div>
  </div>
</template>

<script>
import MediaDelegate from '@/components/media/MediaDelegate.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import IFrameIcon from '@/assets/icons/course/iframe/iframe-icon.svg'
import QuizIcon from '@/assets/icons/course/quiz_icon.svg'
import LockIcon from '@/assets/icons/fill/lock.svg'
import ExerciseIcon from '@/assets/icons/course/exercise/terminal-icon.svg'
import VideoIcon from '@/assets/icons/course/video_icon.svg'
import CONSTANTS from '@/constants'

export default {
  name: 'ContentDelegate',
  components: {
    MediaDelegate,
    ActionLoader,
    IFrameIcon: { ...IFrameIcon, name: 'IFrameIcon' },
    QuizIcon: { ...QuizIcon, name: 'QuizIcon' },
    ExerciseIcon: { ...ExerciseIcon, name: 'ExerciseIcon' },
    VideoIcon: { ...VideoIcon, name: 'VideoIcon' },
    LockIcon: { ...LockIcon, name: 'LockIcon' },
  },
  data() {
    return {
      isContentEngaged: false,
      isContentLoading: false,
    }
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    contentType: {
      type: String,
      required: true,
    },
    isAuthenticated: {
      type: Boolean,
      required: true,
    },
    plansLink: {
      type: String,
      required: true,
    },
  },
  computed: {
    contentTitle() {
      const {
        content: { name },
      } = this
      if (this.contentType === 'quiz') {
        if (this.isAuthenticated) {
          const {
            content: {
              metadata: { quiz_title },
            },
          } = this
          return quiz_title || name || 'Quiz'
        }
        return 'Quiz'
      }
      if (this.contentType === 'exercise') {
        if (this.isAuthenticated) {
          return name || 'Exercise'
        }
        return 'Exercise'
      }
      return name
    },
    currentStatus() {
      const {
        content: { user_status },
      } = this
      return user_status
    },
    contentDuration() {
      const {
        content: { duration_in_seconds },
      } = this
      return this.$duration.durationFromSecs(duration_in_seconds)
    },
    contentIcon() {
      const { contentType } = this
      switch (contentType) {
        case 'quiz':
          return QuizIcon
        case 'video':
          return VideoIcon
        case 'iframe':
          return IFrameIcon
        case 'slides':
          return IFrameIcon
        case 'exercise':
          return ExerciseIcon
        default:
          return VideoIcon
      }
    },
    buttonText() {
      if (this.isContentEngaged) {
        return 'Close'
      }

      const { currentStatus } = this
      if (
        currentStatus &&
        currentStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
      ) {
        return 'Replay'
      }
      return 'Open'
    },
    friendlyContentType() {
      if (this.contentType === 'iframe') {
        return 'Slides'
      }
      return this.contentType
    },
  },
  methods: {
    toggleIsContentEngaged() {
      this.isContentEngaged = !this.isContentEngaged
    },
    setLoader(e) {
      this.isContentLoading = e
    },
  },
}
</script>

<style lang="scss">
.content-delegate {
  width: 100%;
  min-width: 100%;
  height: 100%;
  &__top {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    @include tablet {
      flex-wrap: nowrap;
    }
  }
  &__tile {
    margin: 0 auto 0 0;
    height: 120px;
    width: 200px;
    position: relative;
    @include tablet {
      margin: 0 0 0 50px;
    }
    &--allowed {
      &:hover {
        cursor: pointer;
        opacity: 0.8;
      }
    }
    &--icon {
      position: absolute;
      left: 20px;
      top: 40px;
      fill: var(--white);
    }
  }
  &__details {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 0 10px;
    margin: 0 auto 0 0;
    @include mobile {
      padding: 0 0 0 50px;
    }
    @include desktop {
      padding: 0 0 0 75px;
    }
    &--title {
      font-size: 1.3rem;
      color: var(--white);
      margin: 0 0 20px 0;
      font-family: var(--font-bold);
    }
    &--optional {
      text-transform: uppercase;
      color: var(--orange);
      margin-left: 10px;
      padding-left: 10px;
      border-left: 1px solid var(--gray);
      font-size: 0.8rem;
    }
    &--duration {
      text-transform: lowercase;
    }
  }
  &__btn-wrapper {
    margin: auto 0 0 auto;
  }
  &__spinner-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99999;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.9);
    transition: opacity 0.3s ease;
    .action-loader {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  &__media {
    border: 1px solid var(--gray);
    margin: 50px 0 -15px 50px;
    padding: 25px;
    z-index: 205;
    min-height: 400px;
    position: relative;
  }
  &__media-loader {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: auto;
    width: 100%;
    height: 100%;
  }
}
</style>
